import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
    useGetDealerAddressOrderTypeListQuery,
    useAddDealerAddressOrderTypeMutation,
    useUpdateDealerAddressOrderTypeMutation,
    useDeleteDealerAddressOrderTypeMutation,
    useGetDealerAddressOrderTypeSapListQuery
} from '../../../../../api/endpoints/dealerAddressOrderType';

import { useGetDealerDropdownListQuery } from '../../../../../api/endpoints/dealer';
import { FilterMatchMode } from 'primereact/api';
import { createInitialFilters, convertFiltersToPrimeReactFormat } from '../../../../../utils/filterUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const useCustomerOrderType = () => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentItemId, setCurrentItemId] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);

    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const [lazyParams, setLazyParams] = useState({ page: 1, rows: 10 });
    const [apiFilters, setApiFilters] = useState({});
    const [filters, setFilters] = useState({});

    const usageTypeOptions = useMemo(
        () => [
            { label: th('customerOrderType.service'), value: 1 },
            { label: th('customerOrderType.sales'), value: 2 }
        ],
        [th]
    );

    const filterFields = useMemo(
        () => [
            { field: 'dealerName', headerName: th('customer.dealerName'), type: 'text' },
            { field: 'dealerAddressName', headerName: th('customer.address'), type: 'text' },
            { field: 'orderTypeName', headerName: th('customerOrderType.orderType'), type: 'text' },
            { field: 'usageType', headerName: th('customerOrderType.usageType'), type: 'select', options: usageTypeOptions },
            { field: 'startDate', headerName: th('customer.contractStartDate'), type: 'date' },
            { field: 'endDate', headerName: th('customer.contractEndDate'), type: 'date' },
            { field: 'activeOnly', headerName: th('customer.status'), type: 'boolean' } //0 passive, 1 active, 2 deleted
        ],
        [th, usageTypeOptions]
    );

    const filterTypeMap = useMemo(
        () => ({
            dealerName: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            dealerAddressName: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            orderTypeName: { type: 'text', matchMode: FilterMatchMode.STARTS_WITH },
            usageType: { type: 'number', matchMode: FilterMatchMode.EQUALS },
            startDate: { type: 'date', matchMode: FilterMatchMode.DATE_IS },
            endDate: { type: 'date', matchMode: FilterMatchMode.DATE_IS }
        }),
        []
    );

    const filterFieldNames = useMemo(() => filterFields.map((f) => f.field), [filterFields]);
    const initialFilters = useMemo(() => createInitialFilters(filterFieldNames, filterTypeMap), [filterFieldNames, filterTypeMap]);

    useEffect(() => setFilters(initialFilters), [initialFilters]);

    const {
        data: orderTypeData,
        isLoading,
        refetch
    } = useGetDealerAddressOrderTypeListQuery({
        ActiveOnly: 1,
        pageNumber: lazyParams.page,
        pageSize: lazyParams.rows,
        ...apiFilters
    });

    const { data: dealerOptions = [] } = useGetDealerDropdownListQuery({ PageNumber: 1, PageSize: 100 });
    const { data: sapOrderTypes = [] } = useGetDealerAddressOrderTypeSapListQuery();

    const [addItem] = useAddDealerAddressOrderTypeMutation();
    const [updateItem] = useUpdateDealerAddressOrderTypeMutation();
    const [deleteItem] = useDeleteDealerAddressOrderTypeMutation();

    const resetForm = () => {
        setSelectedItem(null);
        setCurrentItemId(null);
        setEditMode(false);
        setSubmitted(false);
    };

    const openNewDialog = () => {
        resetForm();
        setDialogVisible(true);
    };

    const onSubmit = async (formData) => {
        setSubmitted(true);
        const { dealerId, dealerAddressId, orderTypeId, usageType, contractStartDate, contractEndDate, description, isActive } = formData;

        if (!dealerId || !dealerAddressId || !orderTypeId || usageType === null || !contractStartDate) {
            console.log('❌ Validation failed:', { dealerId, dealerAddressId, orderTypeId, usageType, contractStartDate });
            return;
        }

        const payload = {
            dealerId: Number(dealerId),
            dealerAddressId: Number(dealerAddressId),
            orderTypeId: Number(orderTypeId),
            isActive: isActive,
            usageType: Number(usageType),
            contractStartDate: contractStartDate,
            contractEndDate: contractEndDate,
            description: description || '',
            createdBy: 1,
            createdSession: 'WEB'
        };

        try {
            if (editMode && currentItemId) {
                // Update existing item
                const updatePayload = {
                    id: currentItemId,
                    ...payload
                };
                await updateItem(updatePayload).unwrap();
                toast.current?.show({ severity: 'success', summary: th('messages.updateSuccess'), detail: th('customerOrderType.updateSuccessMessage'), life: 3000 });
            } else {
                await addItem(payload).unwrap();
                toast.current?.show({ severity: 'success', summary: th('messages.saveSuccess'), detail: th('customerOrderType.saveSuccessMessage'), life: 3000 });
            }
            setDialogVisible(false);
            resetForm();
            refetch();
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('messages.saveError'), life: 3000 });
        }
    };

    const handleEdit = (item) => {
        // Directly use the API response structure
        setSelectedItem(item);
        setCurrentItemId(item.id);
        setEditMode(true);
        setSubmitted(false);
        setDialogVisible(true);
    };

    const deleteOrderType = async () => {
        if (!itemToDelete) return;
        try {
            await deleteItem(itemToDelete.id).unwrap();
            toast.current?.show({ severity: 'success', summary: th('messages.deleteSuccess'), detail: th('customerOrderType.deleteSuccessMessage'), life: 3000 });
            refetch();
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('messages.deleteError'), life: 3000 });
        } finally {
            setDeleteConfirmationVisible(false);
            setItemToDelete(null);
        }
    };

    const handleApplyFilters = useCallback(
        (updatedFilters) => {
            const converted = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
            setFilters(converted);

            const newApiFilters = {};
            Object.entries(updatedFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== '' && value !== null) {
                    let processedValue = value;

                    // Handle date fields - use ISO string format like in priceList
                    if ((key === 'startDate' || key === 'endDate') && value instanceof Date) {
                        // Use ISO string format for consistency with priceList filters
                        processedValue = value.toISOString();
                    }
                    if (key === 'activeOnly') {
                        processedValue = value ? 1 : 0;
                    }
                    const pascal = key.charAt(0).toUpperCase() + key.slice(1);
                    newApiFilters[pascal] = processedValue;
                }
            });
            setApiFilters(newApiFilters);
        },
        [filters, filterTypeMap]
    );

    const handleResetFilters = () => {
        setFilters(initialFilters);
        setApiFilters({});
    };

    return {
        toast,
        dialogVisible,
        setDialogVisible,
        openNewDialog,
        resetForm,
        filterFields,
        customFilterTypes: {
            usageType: 'dropdown'
        },
        customFilterOptions: {
            usageType: usageTypeOptions
        },
        handleSearch: () => {},
        handleApplyFilters,
        handleResetFilters,
        deleteConfirmationVisible,
        setDeleteConfirmationVisible,
        deleteOrderType,
        tableProps: {
            data: orderTypeData?.items || [],
            totalRecords: orderTypeData?.totalCount || 0,
            isLoading,
            onPage: (e) => setLazyParams({ page: e.page + 1, rows: e.rows }),
            lazyParams: { first: (lazyParams.page - 1) * lazyParams.rows, rows: lazyParams.rows },
            onEdit: handleEdit,
            onDelete: (item) => {
                setItemToDelete(item);
                setDeleteConfirmationVisible(true);
            },
            resetPagination: false
        },
        formProps: {
            visible: dialogVisible,
            onSubmit,
            sapOrderTypes,
            dealerOptions,
            submitted,
            editMode,
            selectedItem
        }
    };
};

export default useCustomerOrderType;
